import React from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import './styles.scss';

interface BoletoPaymentModalProps {
    show: boolean;
    onHide: () => void;
    boletoUrl?: string;
    subscriptionId: string;
    amount?: number;
    dueDate?: string;
    isTestEnvironment?: boolean;
}

const BoletoPaymentModal: React.FC<BoletoPaymentModalProps> = ({
    show,
    onHide,
    boletoUrl,
    subscriptionId,
    amount,
    dueDate,
    isTestEnvironment = false
}) => {
    const handleOpenBoleto = () => {
        window.open(boletoUrl, '_blank');
    };

    const formatAmount = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value / 100);
    };

    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('pt-BR').format(date);
        } catch {
            return dateString;
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg" className="boleto-payment-modal">
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="bi bi-receipt me-2"></i>
                    Boleto Bancário
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                <Alert variant="success" className="mb-4">
                    <h5 className="alert-heading mb-3">✅ Assinatura criada com sucesso!</h5>
                    <p className="mb-1"><strong>ID da Assinatura:</strong> {subscriptionId}</p>
                    {amount && <p className="mb-1"><strong>Valor:</strong> {formatAmount(amount)}</p>}
                    {dueDate && <p className="mb-0"><strong>Vencimento:</strong> {formatDate(dueDate)}</p>}
                </Alert>

                {!boletoUrl ? (
                    <Alert variant="warning" className="text-start">
                        <h6 className="alert-heading">⚠️ Boleto não disponível</h6>
                        {isTestEnvironment ? (
                            <>
                                <p>Em <strong>ambiente de teste</strong>, o boleto pode não ser gerado pelo gateway de pagamento.</p>
                                <p className="mb-0">Em <strong>produção</strong>, com chaves reais, o boleto será gerado automaticamente e exibido aqui para impressão.</p>
                            </>
                        ) : (
                            <p className="mb-0">Não foi possível gerar o boleto no momento. Tente novamente ou entre em contato com o suporte.</p>
                        )}
                    </Alert>
                ) : (
                    <>
                        <div className="text-center mb-4">
                            <Button 
                                variant="warning" 
                                size="lg" 
                                onClick={handleOpenBoleto}
                                className="mb-3"
                            >
                                <i className="bi bi-printer me-2"></i>
                                Abrir Boleto para Imprimir
                            </Button>
                        </div>

                        <div className="boleto-preview mb-4">
                            <iframe
                                src={boletoUrl}
                                title="Boleto Bancário"
                                className="boleto-iframe"
                                style={{
                                    width: '100%',
                                    height: '500px',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '8px'
                                }}
                            />
                        </div>
                    </>
                )}

                <Alert variant="info">
                    <h6 className="alert-heading">📋 Instruções de Pagamento:</h6>
                    <ul className="mb-0">
                        <li>O boleto pode ser pago em qualquer banco, casa lotérica ou aplicativo bancário</li>
                        <li>O pagamento pode levar até 3 dias úteis para ser confirmado</li>
                        <li>Após o vencimento, poderão ser cobrados juros e multa</li>
                    </ul>
                </Alert>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Fechar
                </Button>
                {boletoUrl && (
                    <Button variant="warning" onClick={handleOpenBoleto}>
                        <i className="bi bi-download me-1"></i>
                        Baixar Boleto
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default BoletoPaymentModal;
